import * as animationData from 'assets/charging_anim.json';
import imgChargedFinal from 'assets/images/img_charged_final.png';
import imgUnplugConnector from 'assets/images/img_unplug_connector.jpg';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import feHelper from 'features/FE/feHelper';
import ChargeTransactionHelper from 'features/ManageChargeTransaction/chargeTransactionHelper';
import { AppDialogHelper } from 'general/components/AppDialog';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import CustomAccordion from 'general/components/Others/CustomAccordion';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppConstants from 'general/constants/AppConstants';
import AppResource from 'general/constants/AppResource';
import ChargeConnectorHelper from 'general/helpers/chargeConnectorHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import { useEffect, useRef, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useSelector } from 'react-redux';

function ChargeTransactionDetailScreen(props) {
  // ---- params ----
  const router = useRouter();
  const { t } = useTranslation();
  const { chargeTransactionId, paymentId } = router.query;
  const refAnimatedDiv = useRef();
  const refCardOverview = useRef();
  const refChildAnimatedDiv = useRef();
  const [chargeTransactionDetail, setChargeTransactionDetail] = useState({});
  const [animatedDivWidth, setAnimatedDivWidth] = useState();
  const [chargeTimeText, setChargeTimeText] = useState('00:00');
  const [chargeTimeLeft, setChargeTimeLeft] = useState(0);
  const [chargeTimeOver, setChargeTimeOver] = useState(0);
  const windowSize = useWindowSize();
  const [chargePricing, setChargePricing] = useState({});
  const refBottom = useRef();
  const [showTransactionInfo, setShowTransactionInfo] = useState(true);
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  useEffect(() => {
    setAnimatedDivWidth(refChildAnimatedDiv.current?.offsetWidth);
  }, [refChildAnimatedDiv.current, windowSize]);

  useEffect(() => {
    if (chargeTransactionDetail?.startTime && chargeTransactionDetail?.endTime) {
      const chargeTimeInSeconds = Utils.calculateChargeTimeInSeconds(
        chargeTransactionDetail.startTime,
        chargeTransactionDetail.endTime
      );
      setChargeTimeText(Utils.convertSecondsToMS(chargeTimeInSeconds));
    }

    let timerApi;
    let timerClock;
    if (
      !ChargeTransactionHelper.isFinished(chargeTransactionDetail.status) &&
      !ChargeTransactionHelper.isCancelled(chargeTransactionDetail.status)
    ) {
      timerApi = setInterval(() => {
        getChargeTransactionDetail();
      }, AppConfigs.timeIntervalGetChargeTransactionDetail);
    }
    if (ChargeTransactionHelper.isCharging(chargeTransactionDetail.status)) {
      timerClock = setInterval(() => {
        const str = Utils.convertSecondsToMS(
          Utils.calculateChargeTimeInSeconds(
            chargeTransactionDetail?.startTime,
            chargeTransactionDetail?.endTime
          )
        );
        setChargeTimeText(str);
      }, 1000);
    }

    if (ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status)) {
      const freeOverchargeDuration = chargeTransactionDetail?.actualPricing?.freeOverchargeDuration || 0;

      timerClock = setInterval(() => {
        const elapsedSeconds = Utils.calculateChargeTimeInSeconds(
          chargeTransactionDetail?.endTime,
          moment()
        );
        const timeLeft = Math.max(0, freeOverchargeDuration - elapsedSeconds);

        setChargeTimeLeft(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(timerClock);
        }
      }, 1000);
    }

    return () => {
      clearInterval(timerApi);
      clearInterval(timerClock);
    };
  }, [chargeTransactionDetail]);

  useEffect(() => {
    let timerClock;

    if (
      chargeTimeLeft === 0 &&
      ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status)
    ) {
      const freeOverchargeDuration = chargeTransactionDetail?.actualPricing?.freeOverchargeDuration || 0;
      const endTimeWithFreeDuration = moment(chargeTransactionDetail?.endTime).add(freeOverchargeDuration, 'seconds');

      timerClock = setInterval(() => {
        const timeOver = Utils.calculateChargeTimeInSeconds(
          endTimeWithFreeDuration,
          moment()
        );
        setChargeTimeOver(timeOver);
      }, 1000);
    }

    return () => clearInterval(timerClock);
  }, [chargeTimeLeft, chargeTransactionDetail]);

  useEffect(() => {
    getChargeTransactionDetail();
  }, [chargeTransactionId]);

  useEffect(() => {
    if (chargeTransactionDetail?.connectorId) {
      getChargeConnectorDetail(chargeTransactionDetail?.connectorId);
    }
  }, [chargeTransactionDetail?.connectorId]);

  // ---- methods ----
  const buildOverviewItemInfo = (icon, value, text) => {
    return (
      <div className="d-flex flex-column align-items-center">
        <i
          className={`${icon} fa-2x d-flex align-items-center justify-content-center rounded-circle`}
          style={{
            color: AppColors.primary,
            backgroundColor: '#B4CBC033',
            width: '50px',
            height: '50px',
          }}
        />
        <span
          className="text-center mt-4"
          style={{
            color: AppColors.primary,
            fontSize: Utils.convertToFontSize(18),
            fontWeight: 600,
          }}
        >
          {value}
        </span>
        <span
          className="text-center"
          style={{
            color: AppColors.gray[800],
            fontWeight: 700,
          }}
        >
          {text}
        </span>
      </div>
    );
  };

  const buildRowItem = (name, value, valueColor = AppColors.gray[800]) => {
    return (
      <div className="d-flex justify-content-between px-4">
        <span
          className="font-weight-bold"
          style={{ color: AppColors.gray[600], fontSize: Utils.convertToFontSize(16) }}
        >
          {name}
        </span>
        <span
          style={{
            color: valueColor,
            fontWeight: '600',
            fontSize: Utils.convertToFontSize(16),
          }}
        >
          {value}
        </span>
      </div>
    );
  };

  const buildSeparator = () => {
    return (
      <div
        className="my-4"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      ></div>
    );
  };

  const getChargeTransactionDetail = () => {
    feHelper.callApiGetTransactionDetail({ transactionId: chargeTransactionId, paymentId: paymentId }).then((data) => {
      console.log({ data });
      setChargeTransactionDetail({ ...chargeTransactionDetail, ...data });
    });
  };

  const getChargeConnectorDetail = (id) => {
    ChargeConnectorHelper.callApiGetChargeConnectorDetail(id).then((data) => {
      const pricing = data.pricing ?? {};
      console.log({ pricing });
      setChargePricing(pricing);
    });
  };

  const handleStopCharge = () => {
    AppDialogHelper.show(
      <span className="font-weight-boldest" style={{ fontSize: Utils.convertToFontSize(20) }}>
        {t('stopCharge')}
      </span>,
      '',
      [
        {
          title: <span style={{ fontSize: Utils.convertToFontSize(18) }}>{t('cancel')}</span>,
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
          },
        },
        {
          title: <span style={{ fontSize: Utils.convertToFontSize(18) }}>{t('confirm')}</span>,
          type: 'negative',
          onClick: async () => {
            AppDialogHelper.hide();
            const res = await feHelper.callApiDeactivateCharger({
              transactionId: chargeTransactionId,
              paymentId: paymentId,
            });
            if (res) {
              const { result, reason } = res;
              if (result == 'success') {
                ToastHelper.showSuccess(t('stopChargerSuccess'));
              } else {
                ToastHelper.showError(reason);
              }
            }
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogError,
      <div className="d-flex flex-column align-items-center">
        <span
          className="text-center"
          style={{
            color: AppColors.gray[600],
            fontSize: Utils.convertToFontSize(16),
            fontWeight: '600',
          }}
        >
          <span style={{ color: AppColors.danger }}>
            Việc dừng sạc giữa chừng sẽ dẫn đến số tiền đã thanh toán còn thừa sẽ không được hoàn
            trả.
          </span>
          <br />
          Để quản lý phí sạc hiệu quả và tránh lãng phí khi không sử dụng hết, quý khách nên sử dụng
          ứng dụng của chúng tôi trong lần sau để theo dõi quá trình sạc và quản lý ví điện tử.
        </span>
      </div>,
      'sm'
    );
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column align-items-center justify-content-center py-5"
      style={{
        backgroundImage: `url(${AppResource.images.evg.bgAuthForm})`,
        backgroundSize: 'cover',
        overflowX: 'hidden',
      }}
    >
      <div
        className="container"
        style={{
          paddingBottom: `${refBottom.current?.offsetHeight}px`,
        }}
      >
        {/* header */}
        <div className="d-flex flex-column align-items-center px-5 py-10">
          <div className="d-flex flex-column align-items-center">
            <img alt="logo" src={AppResource.icons.evg.evgLogoAuth} className="w-175px" />
          </div>
        </div>

        {/* thong tin phien sac */}
        <div
          className="py-5 bg-white d-flex flex-column align-items-center"
          style={{
            border: `1px solid ${AppColors.gray[300]}`,
            borderRadius: '10px',
          }}
        >
          <span
            style={{
              fontWeight: '600',
              fontSize: Utils.returnFontSize(18),
              color: AppColors.gray[800],
            }}
          >
            {t('transactionInfo')}
          </span>

          {chargeTransactionDetail?.status !== AppConstants.chargeTransactionStatus.preparing && (
            <div className="mb-4">
              {/* lottie animation */}
              {chargeTransactionDetail?.status === AppConstants.chargeTransactionStatus.charging ? (
                <Lottie options={defaultOptions} width="70%" />
              ) : (
                <div className="d-flex align-items-center justify-content-center">
                  <img
                    src={imgChargedFinal}
                    style={{
                      width: '70%',
                    }}
                    className=""
                  />
                </div>
              )}

              <div className="text-center text-white mt-5 mb-3">
                <span
                  className={`px-3 py-2 rounded-pill font-weight-bolder
                  bg-${ChargeTransactionHelper.getChargeTransactionStatusColor(
                    chargeTransactionDetail.status
                  )}`}
                  style={{
                    fontSize: Utils.convertToFontSize(16),
                  }}
                >
                  <i className="fa-solid fa-bolt text-white fa-1x mr-2" />
                  {chargeTransactionDetail.status !== AppConstants.chargeTransactionStatus.charging
                    ? `${t(`chargeStatus_${chargeTransactionDetail?.status}`)}`
                    : `${chargeTransactionDetail?.percentComplete}% ${t(
                      `chargeStatus_${chargeTransactionDetail?.status}`
                    )}`}
                </span>
              </div>

              {/* <div
                ref={refAnimatedDiv}
                className={`my-4 rounded-circle overflow-hidden rotating`}
                style={{
                  aspectRatio: '1/1',
                  width: '80%',
                  background:
                    'linear-gradient(180deg, rgba(61, 214, 125, 1) 0%, rgba(28, 116, 188, 1) 100%)',
                  padding: '16px',
                  border: `6px solid ${AppColors.gray[200]}`,
                }}
              >
                <div
                  ref={refChildAnimatedDiv}
                  className="w-100 h-100 bg-white rounded-circle d-flex flex-column align-items-center justify-content-center opacity-0"
                  style={{
                    border: `4px solid ${AppColors.gray[300]}`,
                  }}
                ></div>
              </div>

              <div
                className="bg-white rounded-circle d-flex flex-column align-items-center justify-content-center position-absolute"
                style={{
                  width: `${animatedDivWidth}px`,
                  top: `${refAnimatedDiv.current?.offsetTop + refChildAnimatedDiv.current?.offsetTop + 6
                    }px`,
                  aspectRatio: '1/1',
                  border: `4px solid ${AppColors.gray[300]}`,
                }}
              >
                <i
                  className={`fa-solid fa-bolt fa-5x text-${ChargeTransactionHelper.getChargeTransactionStatusColor(
                    chargeTransactionDetail?.status
                  )}`}
                  style={{ color: AppColors.primary }}
                />
                <span
                  className={`text-${ChargeTransactionHelper.getChargeTransactionStatusColor(
                    chargeTransactionDetail?.status
                  )}`}
                  style={{
                    // color: AppColors.primaryActive,
                    fontWeight: '700',
                    fontSize: Utils.convertToFontSize(34),
                  }}
                >
                  {`${chargeTransactionDetail?.percentComplete ?? 0}%`}
                </span>
                <span
                  className={`text-${ChargeTransactionHelper.getChargeTransactionStatusColor(
                    chargeTransactionDetail?.status
                  )}`}
                  style={{
                    fontWeight: '700',
                    fontSize: Utils.convertToFontSize(16),
                  }}
                >
                  {chargeTransactionDetail?.status
                    ? t(`chargeTransactionStatus_${chargeTransactionDetail.status}`).toUpperCase()
                    : 'N/A'}
                </span>
              </div> */}
            </div>
          )}

          {/* tong quan */}
          {!ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status) &&
            chargeTransactionDetail?.status !== AppConstants.chargeTransactionStatus.preparing && (
              <div className="d-flex mt-2 justify-content-around align-items-center mb-2 w-100">
                {buildOverviewItemInfo(
                  'fa-solid fa-car-circle-bolt',
                  ChargeTransactionHelper.isCharging(chargeTransactionDetail.status)
                    ? `${chargeTransactionDetail.power / 1000} kW`
                    : 'N/A',
                  t('currentPower')
                )}
                {buildOverviewItemInfo(
                  'fa-solid fa-bolt',
                  `${(chargeTransactionDetail.energy / 1000).toFixed(3)} kWh`,
                  t('chargedMeter')
                )}
                {buildOverviewItemInfo(
                  'fa-solid fa-timer',
                  [
                    AppConstants.chargeTransactionStatus.charging,
                    AppConstants.chargeTransactionStatus.finished,
                    AppConstants.chargeTransactionStatus.finishing,
                  ].includes(chargeTransactionDetail?.status)
                    ? chargeTimeText
                    : 'N/A',
                  t('chargedTime')
                )}
              </div>
            )}

          {/* thong bao phien sac ket thuc */}
          {ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status) && (
            <div className="w-100 px-5">
              <div
                className='mt-4'
                style={{
                  backgroundColor: 'rgba(247, 255, 246, 1)',
                  border: `1px solid ${AppColors.primary}`,
                  borderRadius: '5px'
                }}
              >
                <div
                  className="p-5 d-flex align-items-start w-100"
                  style={{
                    borderBottom: `1px solid ${AppColors.gray[300]}`,
                  }}
                >
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center p-3 w-35px"
                    style={{
                      aspectRatio: '1/1',
                      backgroundColor: 'rgb(204, 241, 219)',
                    }}
                  >
                    <i className="fa-regular fa-check" style={{ color: AppColors.primary }} />
                  </div>

                  <div className="d-flex flex-column ml-4">
                    <span
                      style={{
                        color: AppColors.primary,
                        fontWeight: '700',
                        fontSize: Utils.convertToFontSize(20),
                      }}
                    >
                      Phiên sạc kết thúc
                    </span>
                    <span
                      style={{
                        color: AppColors.gray[700],
                        fontWeight: '600',
                        fontSize: Utils.convertToFontSize(16),
                      }}
                    >
                      Đã hoàn tất quá trình sạc. Xin vui lòng rút vòi sạc và cắm lại về trụ để tránh
                      phát sinh thêm chi phí.
                    </span>
                  </div>
                </div>
                {chargeTimeLeft > 0 && (
                  <div className='px-5 py-4 d-flex justify-content-between'>
                    <div className='d-flex align-items-center gap-2'>
                      <img src={AppResource.icons.evg.icClockTimeLeft} />
                      <span
                        style={{
                          color: AppColors.gray[700],
                          fontWeight: '600',
                          fontSize: Utils.convertToFontSize(16),
                        }}
                      >
                        Thời gian chờ miễn phí
                      </span>
                    </div>
                    <div
                      style={{
                        fontWeight: '700',
                        fontSize: Utils.convertToFontSize(22),
                        color: AppColors.primary,
                      }}
                    >
                      {Utils.convertSecondsToMS(chargeTimeLeft)}
                    </div>
                  </div>
                )}

                {chargeTimeOver >= 0 && (
                  (
                    <div className='px-5 py-4 d-flex justify-content-between'>
                      <div className='d-flex align-items-center gap-2'>
                        <img src={AppResource.icons.evg.icClockOvertime} />
                        <span
                          style={{
                            color: AppColors.gray[700],
                            fontWeight: '600',
                            fontSize: Utils.convertToFontSize(16),
                          }}
                        >
                          Sạc quá giờ
                        </span>
                      </div>
                      <div
                        style={{
                          fontWeight: '700',
                          fontSize: Utils.convertToFontSize(22),
                          color: AppColors.danger,
                        }}
                      >
                        {Utils.convertSecondsToMS(chargeTimeOver)}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* thong bao chua cam voi sac */}
          {chargeTransactionDetail?.status === AppConstants.chargeTransactionStatus.preparing && (
            <div className="px-5 w-100">
              <img
                src={imgUnplugConnector}
                className="w-100 mt-4"
                style={{
                  objectFit: 'cover',
                }}
              />
              <div
                className="p-5 d-flex align-items-center mt-3 w-100"
                style={{
                  backgroundColor: 'rgba(255, 246, 246, 1)',
                  border: `1px dashed ${AppColors.danger}`,
                }}
              >
                <img className="w-45px h-45px" src={AppResource.icons.icConnectorBusy} />

                <div className="d-flex flex-column ml-4">
                  <span
                    style={{
                      color: AppColors.danger,
                      fontWeight: '700',
                      fontSize: Utils.convertToFontSize(20),
                    }}
                  >
                    Bạn chưa cắm vòi sạc
                  </span>
                  <span
                    style={{
                      color: AppColors.gray[700],
                      fontWeight: '600',
                      fontSize: Utils.convertToFontSize(16),
                    }}
                  >
                    Cắm vòi sạc vào xe để bắt đầu
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* so tien nap + so tien da su dung */}
          <div className="px-5 w-100 mt-4">
            <div
              className="d-flex w-100 flex-row align-items-center justify-content-center"
              style={{ border: `1px solid ${AppColors.gray[300]}`, borderRadius: '5px' }}
            >
              <div
                className="d-flex flex-column align-items-center flex-grow-1 py-4"
                style={{
                  borderRight: `1px dashed ${AppColors.gray[300]}`,
                }}
              >
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: Utils.convertToFontSize(14),
                    color: AppColors.gray[600],
                  }}
                >
                  Số tiền đã nạp
                </span>
                <div>
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: Utils.convertToFontSize(22),
                      color: AppColors.gray[800],
                    }}
                  >
                    {Utils.formatNumber(chargeTransactionDetail?.prepaidAmount)}
                  </span>
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: Utils.convertToFontSize(14),
                      color: AppColors.gray[800],
                    }}
                  >
                    {chargeTransactionDetail?.currency}
                  </span>
                </div>
              </div>
              <div className="d-flex flex-column align-items-center flex-grow-1">
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: Utils.convertToFontSize(14),
                    color: AppColors.gray[600],
                  }}
                >
                  Số tiền đã dùng
                </span>
                <div>
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: Utils.convertToFontSize(22),
                      color: AppColors.danger,
                    }}
                  >
                    {Utils.formatNumber(chargeTransactionDetail?.estimatedEnergyCost)}
                  </span>
                  <span
                    style={{
                      fontWeight: '700',
                      fontSize: Utils.convertToFontSize(14),
                      color: AppColors.danger,
                    }}
                  >
                    {chargeTransactionDetail?.currency}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-100 px-5">
            {chargeTransactionDetail?.status !== AppConstants.chargeTransactionStatus.preparing && (
              <div className="">
                {ChargeTransactionHelper.isCharging(chargeTransactionDetail?.status) && (
                  <KTBSButton
                    color={KTBSButtonColor.danger}
                    title={t('stopCharge')}
                    buttonTitleFontSize={18}
                    borderRadius={5}
                    buttonTitleFontWeight="600"
                    onPress={handleStopCharge}
                    additionalClassName="w-100 mt-4"
                    iconElement={<i className="fa-solid fa-power-off" />}
                  />
                )}
              </div>
            )}
          </div>

          {/* thong tin phien sac */}
          {!ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status) && (
            <div
              className="w-100 mt-3"
              style={{
                borderTop: `1px solid ${AppColors.gray[300]}`,
                borderBottom: `1px solid ${AppColors.gray[300]}`,
              }}
            >
              <CustomAccordion
                className="border-0"
                title={t('transactionInfo')}
                titleStyle={{
                  fontWeight: '600',
                  fontSize: Utils.returnFontSize(18),
                  color: AppColors.gray[800],
                }}
                collapsible={true}
                showBorderBottom={false}
                contentClassName="pt-0"
                content={
                  <div
                    className="py-4 mt-0 d-flex flex-column"
                    style={{
                      border: `1px solid ${AppColors.gray[300]}`,
                      borderRadius: '5px',
                    }}
                  >
                    {buildRowItem(
                      t('chargeTransactionCode'),
                      chargeTransactionDetail?.transactionCode ?? 'N/A'
                    )}
                    {buildSeparator()}
                    {buildRowItem(
                      t('chargeConnectorCode'),
                      chargeTransactionDetail?.chargeConnector?.connectorCode ?? 'N/A'
                    )}
                    {buildSeparator()}
                    {buildRowItem(
                      t('maximumCapacity'),
                      chargeTransactionDetail?.power != null
                        ? `${chargeTransactionDetail.power / 1000} kW`
                        : 'N/A'
                    )}
                    {buildSeparator()}
                    {buildRowItem(
                      t('voltage'),
                      chargeTransactionDetail?.voltage != null
                        ? `${chargeTransactionDetail.voltage} V`
                        : 'N/A'
                    )}
                    {buildSeparator()}
                    {buildRowItem(
                      t('current'),
                      chargeTransactionDetail?.current != null
                        ? `${chargeTransactionDetail.current} A`
                        : 'N/A'
                    )}
                    {buildSeparator()}
                    {buildRowItem(
                      t('chargePointCode'),
                      chargeTransactionDetail?.chargeConnector?.chargePoint?.pointCode ?? 'N/A'
                    )}
                  </div>
                }
                showExpandTitle={false}
                expandIconColor={AppColors.gray[800]}
              />
            </div>
          )}

          {/* thong tin giao dich */}
          <div className="bg-white mt-4 w-100 px-5">
            <span
              style={{
                fontWeight: '600',
                fontSize: Utils.returnFontSize(18),
                color: AppColors.gray[800],
              }}
            >
              {t('paymentTransactionInfo')}
            </span>

            {chargeTransactionDetail?.status !== AppConstants.chargeTransactionStatus.preparing && (
              <div
                className="py-4 mt-3 d-flex flex-column"
                style={{
                  border: `1px solid ${AppColors.gray[300]}`,
                  borderRadius: '5px',
                }}
              >
                {buildRowItem(
                  t('Số điện đã sạc'),
                  `${(chargeTransactionDetail.energy / 1000).toFixed(0)} kWh`
                )}
                {buildSeparator()}
                {buildRowItem(
                  t('startTime'),
                  Utils.formatDateTimeToString(chargeTransactionDetail?.startTime)
                )}
                {buildSeparator()}
                {buildRowItem(
                  t('endTime'),
                  Utils.formatDateTimeToString(chargeTransactionDetail?.endTime)
                )}
                {buildSeparator()}
                {buildRowItem(t('chargedTime'), `${chargeTimeText} phút`)}
                {buildSeparator()}
                {buildRowItem(
                  t('overtimeWaiting'),
                  `${chargeTransactionDetail?.holdDuration
                    ? Utils.convertSecondsToMS(chargeTransactionDetail?.holdDuration - chargePricing?.freeOverchargeDuration)
                    : chargeTimeOver >= 0 ? Utils.convertSecondsToMS(chargeTimeOver) : 0 ?? 'N/A'} ${t(
                      'minutes'
                    ).toLocaleLowerCase()}`
                )}
              </div>
            )}

            <div
              className="py-4 mt-3 d-flex flex-column"
              style={{
                border: `1px solid ${AppColors.gray[300]}`,
                borderRadius: '5px',
              }}
            >
              {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.FLAT && buildRowItem(
                t('price'),
                chargePricing?.flatPrice
                  ? `${Utils.formatNumber(chargePricing?.flatPrice)} ${chargePricing?.currency}/kWh`
                  : 'N/A'
              )}
              {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS && (
                <>
                  {buildRowItem(
                    t('Giá giờ bình thường'),
                    chargePricing?.normalPrice
                      ? `${Utils.formatNumber(chargePricing.normalPrice)} ${chargePricing.currency}/kWh`
                      : 'N/A'
                  )}
                  {buildSeparator()}
                  {buildRowItem(
                    t('Giá giờ thấp điểm'),
                    chargePricing?.offPeakPrice
                      ? `${Utils.formatNumber(chargePricing.offPeakPrice)} ${chargePricing.currency}/kWh`
                      : 'N/A'
                  )}
                  {buildSeparator()}
                  {buildRowItem(
                    t('Giá giờ cao điểm'),
                    chargePricing?.peakPrice
                      ? `${Utils.formatNumber(chargePricing.peakPrice)} ${chargePricing.currency}/kWh`
                      : 'N/A'
                  )}
                </>
              )}
              {buildSeparator()}
              {buildRowItem(
                t('overtimeFee'),
                chargePricing?.overchargePrice
                  ? `${Utils.formatNumber(chargePricing?.overchargePrice)} ${chargePricing?.currency
                  }/phút`
                  : 'N/A'
              )}
            </div>

            {!ChargeTransactionHelper.isFinishing(chargeTransactionDetail?.status) && (
              <div
                className="py-4 mt-3 d-flex flex-column"
                style={{
                  border: `1px solid ${AppColors.gray[300]}`,
                  borderRadius: '5px',
                }}
              >
                {buildRowItem(
                  t('Tổng thanh toán'),
                  chargeTransactionDetail?.holdDuration && chargePricing?.freeOverchargeDuration
                    ? `${Utils.formatNumber(
                      chargeTransactionDetail?.estimatedEnergyCost +
                      chargePricing?.overchargePrice * (chargeTransactionDetail?.holdDuration - chargePricing?.freeOverchargeDuration) / 60)} 
                    ${chargePricing?.currency}`
                    : 'N/A'
                )}
                {buildSeparator()}
                {buildRowItem(
                  t('remainingAmount'),
                  `${Utils.formatNumber(
                    (chargeTransactionDetail?.prepaidAmount ?? 0) -
                    (chargeTransactionDetail?.estimatedEnergyCost ?? 0) -
                    chargePricing?.overchargePrice * (chargeTransactionDetail?.holdDuration - chargePricing?.freeOverchargeDuration) / 60
                  )} ${chargeTransactionDetail?.currency}`,
                  AppColors.primary
                )}
              </div>
            )}
          </div>

          <div className="w-100 px-5">
            {chargeTransactionDetail?.status !== AppConstants.chargeTransactionStatus.preparing && (
              <div className="mt-3">
                {!ChargeTransactionHelper.isCharging(chargeTransactionDetail?.status) && (
                  <div>
                    <div
                      className="text-center"
                      style={{
                        color: AppColors.danger,
                      }}
                    >
                      <span className="font-weight-boldest">Lưu ý: {''}</span>
                      <span className="font-weight-bold">
                        Số tiền còn dư sẽ không thể hoàn trả lại
                      </span>
                    </div>
                    <KTBSButton
                      color={KTBSButtonColor.success}
                      title={t('goHomepage')}
                      size={KTBSButtonSize.large}
                      buttonTitleFontSize={18}
                      borderRadius={5}
                      buttonTitleFontWeight="600"
                      onPress={() => {
                        router.navigate('/');
                      }}
                      additionalClassName="w-100 mt-4"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChargeTransactionDetailScreen;
